import React from 'react';
import { formatNumberInput, parseItalianNumber } from '../../../utils/formatters';
import './movimentoForm.css';

// ── Tipi ──────────────────────────────────────────────────────────────────
export type Cadenza = 'MENSILE' | 'TRIMESTRALE' | 'SEMESTRALE' | 'ANNUALE';
export type Modalita = 'ANTICIPATA' | 'POSTICIPATA';

export interface FinanziamentoLite {
  id: number;
  societaId: number;
  descrizione: string;
  nomeBanca?: string;
  cadenzaRata: Cadenza;
  numeroFinanziamento?: string;
}

export interface RataDisponibile {
  id: number;
  numeroRata: number;
  dataScadenza: string;
  importoRata: number;
  quotaCapitale: number;
  quotaInteressi: number;
}

export interface FormState {
  societaId: number;
  finanziamentoId: string;
  dataPagamento: string;
  quotaCapitale: string;
  quotaInteressi: string;
  spese: string;
  cadenzaCompetenza: Cadenza;
  modalitaCompetenza: Modalita;
  periodoCompetenzaDa: string;
  periodoCompetenzaA: string;
  rataTeoricaId: string;
  note: string;
}

// ── Costanti ──────────────────────────────────────────────────────────────
export const MESI_PER_CADENZA: Record<Cadenza, number> = {
  MENSILE: 1,
  TRIMESTRALE: 3,
  SEMESTRALE: 6,
  ANNUALE: 12,
};

export const CADENZA_LABEL: Record<Cadenza, string> = {
  MENSILE: 'Mensile',
  TRIMESTRALE: 'Trimestrale',
  SEMESTRALE: 'Semestrale',
  ANNUALE: 'Annuale',
};

// ── Helpers date ──────────────────────────────────────────────────────────
function pad(n: number) { return String(n).padStart(2, '0'); }

export function isoDate(d: Date) {
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

function parseIso(s: string): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

export function fmtData(d: string) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function addMesi(date: Date, mesi: number): Date {
  const d = new Date(date);
  const giorno = d.getUTCDate();
  d.setUTCMonth(d.getUTCMonth() + mesi);
  if (d.getUTCDate() !== giorno) d.setUTCDate(0);
  return d;
}

function addGiorni(date: Date, giorni: number): Date {
  const d = new Date(date);
  d.setUTCDate(d.getUTCDate() + giorni);
  return d;
}

export function derivaPeriodoCompetenza(dataPagamento: string, cadenza: Cadenza, modalita: Modalita) {
  const dp = parseIso(dataPagamento);
  if (!dp) return { da: '', a: '' };
  const mesi = MESI_PER_CADENZA[cadenza];
  if (modalita === 'POSTICIPATA') {
    return { da: isoDate(addMesi(dp, -mesi)), a: isoDate(addGiorni(dp, -1)) };
  }
  return { da: isoDate(dp), a: isoDate(addGiorni(addMesi(dp, mesi), -1)) };
}

export function emptyForm(societaId = 0): FormState {
  const oggi = new Date();
  return {
    societaId,
    finanziamentoId: '',
    dataPagamento: isoDate(new Date(Date.UTC(oggi.getFullYear(), oggi.getMonth(), oggi.getDate()))),
    quotaCapitale: '',
    quotaInteressi: '',
    spese: formatNumberInput(0),
    cadenzaCompetenza: 'MENSILE',
    modalitaCompetenza: 'ANTICIPATA',
    periodoCompetenzaDa: '',
    periodoCompetenzaA: '',
    rataTeoricaId: '',
    note: '',
  };
}

/**
 * Costruisce un FormState già precompilato a partire da una rata teorica del
 * piano di ammortamento (usato dal pulsante "Contabilizza" nella scheda
 * finanziamento). La rata teorica viene preselezionata e il periodo di
 * competenza derivato da data scadenza + cadenza.
 */
export function prefillFormDaRata(opts: {
  societaId: number;
  finanziamentoId: number;
  cadenza: Cadenza;
  rata: {
    id: number;
    dataScadenza: string;
    quotaCapitale: number;
    quotaInteressi: number;
    speseIncasso: number;
  };
}): FormState {
  const modalita: Modalita = 'ANTICIPATA';
  const dataPagamento = (opts.rata.dataScadenza || '').slice(0, 10);
  const periodo = derivaPeriodoCompetenza(dataPagamento, opts.cadenza, modalita);
  return {
    societaId: opts.societaId,
    finanziamentoId: String(opts.finanziamentoId),
    dataPagamento,
    quotaCapitale: formatNumberInput(opts.rata.quotaCapitale),
    quotaInteressi: formatNumberInput(opts.rata.quotaInteressi),
    spese: formatNumberInput(opts.rata.speseIncasso ?? 0),
    cadenzaCompetenza: opts.cadenza,
    modalitaCompetenza: modalita,
    periodoCompetenzaDa: periodo.da,
    periodoCompetenzaA: periodo.a,
    rataTeoricaId: String(opts.rata.id),
    note: '',
  };
}

// ── Validazione + payload ───────────────────────────────────────────────────
/** Ritorna un messaggio di errore se il form non è valido, altrimenti null. */
export function validaFormMovimento(form: FormState): string | null {
  if (!form.finanziamentoId) return 'Seleziona un finanziamento';
  if (!form.dataPagamento) return 'Data pagamento obbligatoria';
  const qc = parseItalianNumber(form.quotaCapitale || '0');
  const qi = parseItalianNumber(form.quotaInteressi || '0');
  const sp = parseItalianNumber(form.spese || '0');
  if (!Number.isFinite(qc) || qc < 0) return 'Quota capitale non valida';
  if (!Number.isFinite(qi) || qi < 0) return 'Quota interessi non valida';
  if (!Number.isFinite(sp) || sp < 0) return 'Spese non valide';
  if (!form.periodoCompetenzaDa || !form.periodoCompetenzaA) return 'Periodo competenza obbligatorio';
  if (new Date(form.periodoCompetenzaA) < new Date(form.periodoCompetenzaDa)) {
    return 'Periodo competenza: data fine deve essere >= data inizio';
  }
  return null;
}

/** Costruisce il payload per POST/PUT a partire dal form (assume già validato). */
export function buildPayloadMovimento(form: FormState) {
  const qc = parseItalianNumber(form.quotaCapitale || '0');
  const qi = parseItalianNumber(form.quotaInteressi || '0');
  const sp = parseItalianNumber(form.spese || '0');
  return {
    finanziamentoId: Number(form.finanziamentoId),
    dataPagamento: form.dataPagamento,
    quotaCapitale: qc,
    quotaInteressi: qi,
    spese: sp,
    cadenzaCompetenza: form.cadenzaCompetenza,
    modalitaCompetenza: form.modalitaCompetenza,
    periodoCompetenzaDa: form.periodoCompetenzaDa,
    periodoCompetenzaA: form.periodoCompetenzaA,
    rataTeoricaId: form.rataTeoricaId ? Number(form.rataTeoricaId) : null,
    note: form.note || null,
  };
}

// ── Form movimento ──────────────────────────────────────────────────────────
export interface FormMovimentoProps {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  finanziamentiLista: FinanziamentoLite[];
  rateDisponibili: RataDisponibile[];
  disabledFinanziamento: boolean;
  onChangeFinanziamento: (id: string) => void;
  onTouchPeriodo: () => void;
  ricalcolaPeriodo: (next: FormState) => FormState;
  error: string;
}

export function FormMovimento(props: FormMovimentoProps) {
  const { form, setForm, finanziamentiLista, rateDisponibili, disabledFinanziamento, onChangeFinanziamento, onTouchPeriodo, ricalcolaPeriodo, error } = props;

  const updateForm = (patch: Partial<FormState>, opts: { ricalcola?: boolean } = {}) => {
    setForm(prev => {
      let next = { ...prev, ...patch };
      if (opts.ricalcola) next = ricalcolaPeriodo(next);
      return next;
    });
  };

  // ── Campi importo: formato italiano "00.000,00" (testo) ──────────────────
  type MoneyKey = 'quotaInteressi' | 'quotaCapitale' | 'spese';
  const onMoneyChange = (key: MoneyKey) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [key]: e.target.value }));
  // In focus: rimuove i separatori delle migliaia per agevolare l'editing.
  const onMoneyFocus = (key: MoneyKey) => () =>
    setForm(f => {
      if (!f[key].trim()) return f;
      const plain = formatNumberInput(f[key], false);
      return plain ? { ...f, [key]: plain } : f;
    });
  // In blur: riformatta con separatori delle migliaia (es. "6.410,58").
  const onMoneyBlur = (key: MoneyKey) => () =>
    setForm(f => {
      if (!f[key].trim()) return f;
      const grouped = formatNumberInput(f[key], true);
      return grouped ? { ...f, [key]: grouped } : f;
    });

  return (
    <div className="mov-fin-form">
      {error && <div className="bop-inline-error">{error}</div>}

      <div className="bop-form-row">
        <div className="bop-form-group bop-form-group--wide">
          <label className="bop-label">Finanziamento</label>
          <select
            className="bop-select"
            value={form.finanziamentoId}
            onChange={e => onChangeFinanziamento(e.target.value)}
            disabled={disabledFinanziamento}
          >
            <option value="">— Seleziona —</option>
            {finanziamentiLista.map(f => (
              <option key={f.id} value={f.id}>
                {f.descrizione}{f.nomeBanca ? ` — ${f.nomeBanca}` : ''}{f.numeroFinanziamento ? ` (n. ${f.numeroFinanziamento})` : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="bop-form-row">
        <div className="bop-form-group">
          <label className="bop-label">Data pagamento</label>
          <input
            type="date"
            className="bop-input"
            value={form.dataPagamento}
            onChange={e => updateForm({ dataPagamento: e.target.value }, { ricalcola: true })}
          />
        </div>
        <div className="bop-form-group">
          <label className="bop-label">Cadenza competenza</label>
          <select
            className="bop-select"
            value={form.cadenzaCompetenza}
            onChange={e => updateForm({ cadenzaCompetenza: e.target.value as Cadenza }, { ricalcola: true })}
          >
            <option value="MENSILE">Mensile</option>
            <option value="TRIMESTRALE">Trimestrale</option>
            <option value="SEMESTRALE">Semestrale</option>
            <option value="ANNUALE">Annuale</option>
          </select>
        </div>
        <div className="bop-form-group">
          <label className="bop-label">Modalità</label>
          <select
            className="bop-select"
            value={form.modalitaCompetenza}
            onChange={e => updateForm({ modalitaCompetenza: e.target.value as Modalita }, { ricalcola: true })}
          >
            <option value="ANTICIPATA">Anticipata</option>
            <option value="POSTICIPATA">Posticipata</option>
          </select>
        </div>
      </div>

      <div className="bop-form-row">
        <div className="bop-form-group">
          <label className="bop-label">Quota interessi (€)</label>
          <input
            type="text"
            inputMode="decimal"
            className="bop-input"
            value={form.quotaInteressi}
            onChange={onMoneyChange('quotaInteressi')}
            onFocus={onMoneyFocus('quotaInteressi')}
            onBlur={onMoneyBlur('quotaInteressi')}
          />
        </div>
        <div className="bop-form-group">
          <label className="bop-label">Quota capitale (€)</label>
          <input
            type="text"
            inputMode="decimal"
            className="bop-input"
            value={form.quotaCapitale}
            onChange={onMoneyChange('quotaCapitale')}
            onFocus={onMoneyFocus('quotaCapitale')}
            onBlur={onMoneyBlur('quotaCapitale')}
          />
        </div>
        <div className="bop-form-group">
          <label className="bop-label">Spese incasso (€)</label>
          <input
            type="text"
            inputMode="decimal"
            className="bop-input"
            value={form.spese}
            onChange={onMoneyChange('spese')}
            onFocus={onMoneyFocus('spese')}
            onBlur={onMoneyBlur('spese')}
          />
        </div>
      </div>

      <div className="bop-form-row">
        <div className="bop-form-group">
          <label className="bop-label">Periodo competenza — da</label>
          <input
            type="date"
            className="bop-input"
            value={form.periodoCompetenzaDa}
            onChange={e => { onTouchPeriodo(); updateForm({ periodoCompetenzaDa: e.target.value }); }}
          />
        </div>
        <div className="bop-form-group">
          <label className="bop-label">Periodo competenza — a</label>
          <input
            type="date"
            className="bop-input"
            value={form.periodoCompetenzaA}
            onChange={e => { onTouchPeriodo(); updateForm({ periodoCompetenzaA: e.target.value }); }}
          />
        </div>
        <div className="bop-form-group">
          <label className="bop-label">&nbsp;</label>
          <span className="bop-text-muted mov-fin-form-hint">Derivato automaticamente dalla data e cadenza. Editabile.</span>
        </div>
      </div>

      <div className="bop-form-row">
        <div className="bop-form-group bop-form-group--wide">
          <label className="bop-label">Rata teorica corrispondente</label>
          <select
            className="bop-select"
            value={form.rataTeoricaId}
            onChange={e => setForm(f => ({ ...f, rataTeoricaId: e.target.value }))}
            disabled={!form.finanziamentoId}
          >
            <option value="">Nessuna (movimento extra-piano)</option>
            {rateDisponibili.map(r => (
              <option key={r.id} value={r.id}>
                Rata n. {r.numeroRata} — scadenza {fmtData(r.dataScadenza)} — €{r.importoRata.toFixed(2)} (int. €{r.quotaInteressi.toFixed(2)} / cap. €{r.quotaCapitale.toFixed(2)})
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="bop-form-row">
        <div className="bop-form-group bop-form-group--wide">
          <label className="bop-label">Note</label>
          <textarea
            className="bop-input"
            rows={2}
            value={form.note}
            onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
          />
        </div>
      </div>
    </div>
  );
}
